import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import AppLayout from "../components/AppLayout";
import BottomNav from "../components/BottomNav";
import { ArrowLeft, Sparkles, Trash2, Loader2, Calendar, Heart } from "lucide-react";

export default function DreamDetailPage() {
  const { dreamId } = useParams();
  const { api } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const navigate = useNavigate();
  const [dream, setDream] = useState(null);
  const [loading, setLoading] = useState(true);
  const [interpreting, setInterpreting] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    api().get(`/dreams/${dreamId}`)
      .then((r) => setDream(r.data))
      .catch(() => navigate("/journal"))
      .finally(() => setLoading(false));
  }, [dreamId, api, navigate]);

  const handleInterpret = async () => {
    setInterpreting(true);
    try {
      const res = await api().post("/interpret", { dream_text: dream.content });
      setDream((d) => ({ ...d, interpretation: res.data.interpretation }));
      await api().put(`/dreams/${dreamId}`, { title: dream.title, content: dream.content, emotions: dream.emotions, tags: dream.tags });
    } catch (err) {
      console.error(err);
    } finally {
      setInterpreting(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Supprimer ce rêve ?")) return;
    setDeleting(true);
    try {
      await api().delete(`/dreams/${dreamId}`);
      navigate("/journal");
    } catch (err) {
      console.error(err);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    return d.toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-screen">
          <Loader2 size={24} className="animate-spin text-amber-400" />
        </div>
        <BottomNav />
      </AppLayout>
    );
  }

  if (!dream) return null;

  return (
    <AppLayout>
      <div className="px-5 pt-12 pb-24 scrollbar-hide overflow-y-auto min-h-screen">
        <div className="flex items-center justify-between mb-6">
          <button
            data-testid="dream-detail-back"
            onClick={() => navigate("/journal")}
            className="p-2 rounded-full hover:bg-white/10 transition-colors"
          >
            <ArrowLeft size={20} className="text-white" />
          </button>
          <button
            data-testid="dream-delete-button"
            onClick={handleDelete}
            disabled={deleting}
            className="p-2 rounded-full hover:bg-red-500/20 transition-colors text-slate-500 hover:text-red-400"
          >
            <Trash2 size={18} />
          </button>
        </div>

        <div className="animate-fadeInUp">
          <h1 data-testid="dream-detail-title" className="font-heading text-2xl font-bold text-white drop-shadow-lg">{dream.title}</h1>
          <div className="flex items-center gap-2 mt-2 text-slate-400">
            <Calendar size={14} />
            <span className="text-xs">{formatDate(dream.created_at)}</span>
          </div>

          {dream.emotions?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-4">
              {dream.emotions.map((em) => (
                <span key={em} className="flex items-center gap-1 px-3 py-1 rounded-full text-xs bg-amber-500/20 text-amber-400">
                  <Heart size={10} /> {em}
                </span>
              ))}
            </div>
          )}

          {dream.tags?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {dream.tags.map((tag) => (
                <span key={tag} className="px-3 py-1 rounded-full text-xs bg-purple-500/20 text-purple-400">
                  {tag}
                </span>
              ))}
            </div>
          )}

          <div className="glass-card p-5 mt-6">
            <p className="text-white/90 text-sm leading-relaxed whitespace-pre-wrap">{dream.content}</p>
          </div>

          {dream.interpretation ? (
            <div data-testid="dream-interpretation-display" className="glass-card p-5 mt-4">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles size={14} className="text-purple-400" />
                <p className="text-xs text-purple-400 uppercase tracking-[0.15em] font-medium">Interprétation</p>
              </div>
              <p className="text-white/90 text-sm leading-relaxed whitespace-pre-wrap">{dream.interpretation}</p>
            </div>
          ) : (
            <button
              data-testid="dream-interpret-button"
              onClick={handleInterpret}
              disabled={interpreting}
              className="w-full btn-gold py-3 mt-6 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {interpreting ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Interprétation en cours...
                </>
              ) : (
                <>
                  <Sparkles size={16} />
                  Interpréter ce rêve
                </>
              )}
            </button>
          )}
        </div>
      </div>
      <BottomNav />
    </AppLayout>
  );
}
